import { makeStyles } from "@material-ui/core";
import { useContext, useEffect, useReducer, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Endpoint } from "../../api/base";

import { getFieldResourceJson } from "../../components/shared/helpers/helper";
import MapIcon from "@mui/icons-material/Map";

import AddFieldResource from "./AddFieldResource";
import FieldResourceTable from "./FieldResourceTable";
import { viewportContext } from "../../context/ViewportContext";
import { StoreContext } from "../../context/StoreContext";
import FieldResourceCoverageMap from "../../components/FieldResourceView/Map";
import BottomDrawer from "../../components/shared/BottomDrawer";
import RightDrawer from "../../components/shared/RightDrawer";
import { Typography, Tooltip } from "@mui/material";
import { useToast } from "../../context/ToastContext";
import { tokens } from "../../theme/tokens";

import {
  StyledIconButton,
  StyledTabButton,
} from "../../components/shared/helpers/GlobalStylesv5";

const useStyle = makeStyles((theme) => ({
  caseContainer: {
    [theme.breakpoints.down("md")]: {
      width: "90%",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: tokens.space[3],
    padding: tokens.space[4],
  },
  title: {
    margin: "0px",
    fontWeight: 600,
    color: tokens.color.text.primary,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.space[3],
  },
  content: {
    padding: `0px ${tokens.space[4]} ${tokens.space[6]}`,
  },
}));

const BASE_PATH = "/app/field-resource";

const initialState = {
  drawerOpen: false,
  mapOpen: false,
  fieldResourceList: [],
  initState: getFieldResourceJson(),
  loading: true,
};

function reducer(state, action) {
  switch (action.type) {
    case "setDrawerOpen":
      return { ...state, drawerOpen: action.payload };
    case "setMapOpen":
      return { ...state, mapOpen: action.payload };
    case "setFieldResourceList":
      return { ...state, fieldResourceList: action.payload };
    case "setInitState":
      return { ...state, initState: action.payload };
    case "setLoading":
      return { ...state, loading: action.payload };
    case "openDrawerWithData":
      return {
        ...state,
        initState: action.payload,
        drawerOpen: true,
      };
    case "closeDrawer":
      return { ...state, drawerOpen: false };
    default:
      throw new Error("Something went wrong!");
  }
}

function FieldResourceView() {
  const classes = useStyle();
  const history = useHistory();
  const { action, id } = useParams();

  const [state, dispatch] = useReducer(reducer, initialState, undefined);
  const { showToast } = useToast();
  const { tallScreen } = useContext(viewportContext);
  const { fieldResources, setFieldResources } = useContext(StoreContext);

  // Load field resources from context
  useEffect(() => {
    if (!fieldResources) {
      dispatch({ type: "setLoading", payload: true });
      return;
    }
    dispatch({ type: "setFieldResourceList", payload: fieldResources });
    dispatch({ type: "setLoading", payload: false });
  }, [fieldResources]);

  // Handle URL-based routing for drawer and map
  useEffect(() => {
    if (!fieldResources) return;

    const defaultMeta = {
      company_name: "",
      contact_name: "",
      county: "",
      coverage_scope: "",
      notes: "",
      recommend: "",
      w9_on_file: "",
      website: "",
    };

    if (action === "add") {
      const newResource = getFieldResourceJson();
      dispatch({
        type: "openDrawerWithData",
        payload: { ...newResource, meta: { ...defaultMeta } },
      });
    } else if (action === "edit" && id) {
      const resource = fieldResources.find((r) => r.id === id);
      if (resource) {
        const initState = {
          ...resource,
          meta: { ...defaultMeta, ...(resource.meta || {}) },
        };
        dispatch({ type: "openDrawerWithData", payload: initState });
      } else {
        // Resource not found, redirect to base
        history.replace(BASE_PATH);
      }
    } else if (action === "map") {
      dispatch({ type: "setMapOpen", payload: true });
    } else {
      // No action or unknown action - close everything
      dispatch({ type: "closeDrawer" });
      dispatch({ type: "setMapOpen", payload: false });
    }
  }, [action, id, fieldResources, history]);

  const openDrawer = (val) => {
    if (val.id === "") {
      history.push(`${BASE_PATH}/add`);
    } else {
      history.push(`${BASE_PATH}/edit/${val.id}`);
    }
  };

  const closeDrawer = () => {
    history.push(BASE_PATH);
  };

  const openMap = () => {
    history.push(`${BASE_PATH}/map`);
  };

  const closeMap = () => {
    history.push(BASE_PATH);
  };


  const handleDelete = useCallback(
    (resourceId) => {
      Endpoint.deleteFieldResource(resourceId)
        .then(() => {
          showToast("Field Resource deleted successfully.", "success");
          setFieldResources((prev) =>
            prev ? prev.filter((r) => r.id !== resourceId) : prev,
          );
        })
        .catch(() => {
          showToast("Sorry something went wrong, please try again later.", "error");
        });
    },
    [showToast, setFieldResources],
  );

  const getDrawerTitle = () => {
    return state.initState.id === ""
      ? "Add Field Resource"
      : "Edit Field Resource";
  };

  return (
    <>
      <div>
        <div className={classes.header}>
          <Typography variant="h6" className={classes.title}>
            Field Resource Information
          </Typography>
          <div className={classes.actions}>
            {state.fieldResourceList.length > 0 && (
              <Tooltip
                title={state.mapOpen ? "Hide coverage map" : "Show coverage map"}
              >
                <StyledIconButton
                  onClick={openMap}
                  aria-pressed={state.mapOpen}
                  sx={{
                    color: state.mapOpen
                      ? tokens.color.text.primary
                      : tokens.color.text.secondary,
                    backgroundColor: state.mapOpen
                      ? tokens.color.surface.sunken
                      : "transparent",
                  }}
                >
                  <MapIcon />
                </StyledIconButton>
              </Tooltip>
            )}

            <StyledTabButton
              onClick={() => {
                openDrawer(getFieldResourceJson());
              }}
            >
              Add Field Resource
            </StyledTabButton>
          </div>
        </div>

        <div
          className={`${classes.content} ${
            tallScreen ? "" : classes.caseContainer
          }`}
        >
          <FieldResourceTable
            setInitialValues={openDrawer}
            onDelete={handleDelete}
            data={state.fieldResourceList}
            loading={state.loading}
          />
        </div>
      </div>

      <RightDrawer
        open={state.drawerOpen}
        onClose={closeDrawer}
        title={getDrawerTitle()}
      >
        <AddFieldResource initState={state.initState} onClose={closeDrawer} />
      </RightDrawer>

      <BottomDrawer
        open={state.mapOpen}
        onClose={closeMap}
        title={
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Field Resource Coverage Map
          </Typography>
        }
      >
        <FieldResourceCoverageMap fieldResourceList={state.fieldResourceList} />
      </BottomDrawer>
    </>
  );
}

export default FieldResourceView;
